import { useEffect, useState } from 'react';
import { Plus, Trash2, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { fetchProfile, updateProfile, addEducation, deleteEducation } from '../../utils/profile';
import { INDIAN_STATES_AND_UTS } from '../../constants/indianStates';
import { useAuth } from '../../contexts/AuthContext';
import usePageTitle from '../../hooks/usePageTitle';

const PHONE_REGEX = /^\d{10}$/;

const FILMMAKING_INTERESTS = [
  'Direction',
  'Editing',
  'Cinematography',
  'Screenwriting',
  'Sound Design',
  'Production',
  'Not sure yet',
];

const EMPTY_EDUCATION = {
  institution: '',
  degree: '',
  field_of_study: '',
  start_year: new Date().getFullYear() - 1,
  end_year: new Date().getFullYear(),
  currently_studying: false,
  grade_or_cgpa: '',
  city: '',
};

const DashboardProfile = () => {
  usePageTitle('Profile');
  const { refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEducationForm, setShowEducationForm] = useState(false);
  const [educationForm, setEducationForm] = useState(EMPTY_EDUCATION);
  const [detailsForm, setDetailsForm] = useState({
    name: '',
    phone: '',
    city: '',
    state: '',
    date_of_birth: '',
    bio: '',
    portfolio_url: '',
    linkedin_url: '',
    filmmaking_interest: '',
  });

  const loadProfile = async () => {
    try {
      const data = await fetchProfile();
      setProfile(data);
      setDetailsForm((prev) => ({
        ...prev,
        name: data.name || '',
        phone: data.phone || '',
        city: data.city || '',
        state: data.state || '',
        date_of_birth: data.date_of_birth || '',
        bio: data.bio || '',
        portfolio_url: data.portfolio_url || '',
        linkedin_url: data.linkedin_url || '',
        filmmaking_interest: data.filmmaking_interest || '',
      }));
    } catch {
      toast.error('Could not load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setDetailsForm({ ...detailsForm, phone: digits });
  };

  const handleSaveDetails = async (e) => {
    e.preventDefault();
    if (detailsForm.phone && !PHONE_REGEX.test(detailsForm.phone)) {
      toast.error('Phone must be exactly 10 digits (without +91)');
      return;
    }
    if (!detailsForm.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const data = await updateProfile(detailsForm);
      setProfile(data);
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || 'Failed to save profile');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to save profile');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddEducation = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...educationForm,
        start_year: Number(educationForm.start_year),
        end_year: educationForm.currently_studying ? null : Number(educationForm.end_year),
        grade_or_cgpa: educationForm.grade_or_cgpa || null,
        city: educationForm.city || null,
      };
      const data = await addEducation(payload);
      setProfile(data);
      setEducationForm(EMPTY_EDUCATION);
      setShowEducationForm(false);
      toast.success('Education added');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to add education');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEducation = async (educationId) => {
    try {
      const data = await deleteEducation(educationId);
      setProfile(data);
      toast.success('Education removed');
    } catch {
      toast.error('Could not remove education');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t border-b border-[#A80D1A]" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <p className="chapter-label mb-4">PROFILE</p>
      <h1 className="display-heading text-4xl md:text-5xl text-white mb-10">
        Your <em>profile.</em>
      </h1>

      <section className="mb-12">
        <h2 className="font-serif text-xl text-white mb-6">Account</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="field-label">Email</label>
            <p className="text-base text-zinc-400 border border-white/10 bg-[#0D0D0D] px-4 py-3">
              {profile?.email}
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <h2 className="font-serif text-xl text-white flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-[#C8102E]" />
            Education
          </h2>
          {!showEducationForm && (
            <button
              type="button"
              onClick={() => setShowEducationForm(true)}
              className="btn-cine-outline inline-flex text-xs py-2 px-4"
              data-testid="add-education-btn"
            >
              <Plus className="w-4 h-4" />
              Add education
            </button>
          )}
        </div>

        {profile?.education?.length > 0 && (
          <div className="space-y-4 mb-6">
            {profile.education.map((edu) => (
              <div
                key={edu.education_id}
                className="cine-card p-6 border border-white/5 flex justify-between gap-4"
                data-testid={`education-${edu.education_id}`}
              >
                <div>
                  <h3 className="font-serif text-lg text-white mb-1">{edu.institution}</h3>
                  <p className="text-sm text-[#C8102E] mb-2">
                    {edu.degree} · {edu.field_of_study}
                  </p>
                  <p className="text-xs text-zinc-500 tracking-wide">
                    {edu.start_year}
                    {edu.currently_studying ? ' - Present' : edu.end_year ? ` - ${edu.end_year}` : ''}
                    {edu.city ? ` · ${edu.city}` : ''}
                    {edu.grade_or_cgpa ? ` · ${edu.grade_or_cgpa}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteEducation(edu.education_id)}
                  className="text-zinc-600 hover:text-[#C8102E] transition-colors shrink-0"
                  aria-label="Remove education"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {profile?.education?.length === 0 && !showEducationForm && (
          <p className="text-sm text-zinc-500 mb-6">
            Add your school or university details - we use this for internship matching and program recommendations.
          </p>
        )}

        {showEducationForm && (
          <form onSubmit={handleAddEducation} className="cine-card p-6 md:p-8 border border-white/5 space-y-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="field-label">University / Institution *</label>
                <input
                  required
                  value={educationForm.institution}
                  onChange={(e) => setEducationForm({ ...educationForm, institution: e.target.value })}
                  className="field-input"
                  placeholder="e.g. FTII, Whistling Woods"
                />
              </div>
              <div>
                <label className="field-label">Degree *</label>
                <input
                  required
                  value={educationForm.degree}
                  onChange={(e) => setEducationForm({ ...educationForm, degree: e.target.value })}
                  className="field-input"
                  placeholder="e.g. B.A., Diploma"
                />
              </div>
              <div>
                <label className="field-label">Field of study *</label>
                <input
                  required
                  value={educationForm.field_of_study}
                  onChange={(e) => setEducationForm({ ...educationForm, field_of_study: e.target.value })}
                  className="field-input"
                  placeholder="e.g. Film & Television"
                />
              </div>
              <div>
                <label className="field-label">Start year *</label>
                <input
                  required
                  type="number"
                  min={1950}
                  max={2100}
                  value={educationForm.start_year}
                  onChange={(e) => setEducationForm({ ...educationForm, start_year: e.target.value })}
                  className="field-input"
                />
              </div>
              <div>
                <label className="field-label">End year {!educationForm.currently_studying && '*'}</label>
                <input
                  required={!educationForm.currently_studying}
                  disabled={educationForm.currently_studying}
                  type="number"
                  min={1950}
                  max={2100}
                  value={educationForm.end_year}
                  onChange={(e) => setEducationForm({ ...educationForm, end_year: e.target.value })}
                  className="field-input disabled:opacity-40"
                />
              </div>
              <div>
                <label className="field-label">Grade / CGPA</label>
                <input
                  value={educationForm.grade_or_cgpa}
                  onChange={(e) => setEducationForm({ ...educationForm, grade_or_cgpa: e.target.value })}
                  className="field-input"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="field-label">City</label>
                <input
                  value={educationForm.city}
                  onChange={(e) => setEducationForm({ ...educationForm, city: e.target.value })}
                  className="field-input"
                  placeholder="Optional"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input
                type="checkbox"
                checked={educationForm.currently_studying}
                onChange={(e) =>
                  setEducationForm({ ...educationForm, currently_studying: e.target.checked })
                }
                className="accent-[#C8102E]"
              />
              I am currently studying here
            </label>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving} className="btn-cine-primary">
                {saving ? 'Saving…' : 'Save education'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowEducationForm(false);
                  setEducationForm(EMPTY_EDUCATION);
                }}
                className="btn-cine-outline"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>

      <section>
        <h2 className="font-serif text-xl text-white mb-2">More about you</h2>
        <p className="text-sm text-zinc-500 mb-6">
          Help us match you with internships and learning paths.
        </p>
        <form onSubmit={handleSaveDetails} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="field-label">Name *</label>
              <input
                required
                value={detailsForm.name}
                onChange={(e) => setDetailsForm({ ...detailsForm, name: e.target.value })}
                className="field-input"
              />
            </div>
            <div>
              <label className="field-label">Phone</label>
              <div className="flex">
                <span className="inline-flex items-center px-4 border border-r-0 border-white/10 bg-[#141414] text-sm text-zinc-400 shrink-0">
                  +91
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={detailsForm.phone}
                  onChange={handlePhoneChange}
                  className="field-input rounded-none border-l-0 flex-1"
                  placeholder="10-digit mobile number"
                />
              </div>
              {detailsForm.phone && detailsForm.phone.length !== 10 && (
                <p className="text-xs text-[#C8102E] mt-1">Enter exactly 10 digits</p>
              )}
            </div>
            <div>
              <label className="field-label">Date of birth</label>
              <input
                type="date"
                value={detailsForm.date_of_birth}
                onChange={(e) => setDetailsForm({ ...detailsForm, date_of_birth: e.target.value })}
                className="field-input"
              />
            </div>
            <div>
              <label className="field-label">State</label>
              <select
                value={detailsForm.state}
                onChange={(e) => setDetailsForm({ ...detailsForm, state: e.target.value })}
                className="field-input"
              >
                <option value="">Select state…</option>
                {INDIAN_STATES_AND_UTS.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">City</label>
              <input
                value={detailsForm.city}
                onChange={(e) => setDetailsForm({ ...detailsForm, city: e.target.value })}
                className="field-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Primary filmmaking interest</label>
              <select
                value={detailsForm.filmmaking_interest}
                onChange={(e) => setDetailsForm({ ...detailsForm, filmmaking_interest: e.target.value })}
                className="field-input"
              >
                <option value="">Select…</option>
                {FILMMAKING_INTERESTS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Portfolio URL</label>
              <input
                type="url"
                value={detailsForm.portfolio_url}
                onChange={(e) => setDetailsForm({ ...detailsForm, portfolio_url: e.target.value })}
                className="field-input"
                placeholder="https://"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">LinkedIn URL</label>
              <input
                type="url"
                value={detailsForm.linkedin_url}
                onChange={(e) => setDetailsForm({ ...detailsForm, linkedin_url: e.target.value })}
                className="field-input"
                placeholder="https://linkedin.com/in/…"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">About you</label>
              <textarea
                rows={4}
                value={detailsForm.bio}
                onChange={(e) => setDetailsForm({ ...detailsForm, bio: e.target.value })}
                className="field-input resize-y min-h-[100px]"
                placeholder="Tell us about your filmmaking journey and goals…"
              />
            </div>
          </div>
          <button type="submit" disabled={saving} className="btn-cine-primary mt-4" data-testid="save-profile">
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </form>
      </section>
    </div>
  );
};

export default DashboardProfile;
